import LexicalParser from 'app/services/formula/parser/lexicalParser';
import SyntaxParser, { syntaxParser } from 'app/services/formula/parser/syntaxParser';
import { formatExpressionDocument } from 'app/shared/componentsRestyled/common/CodeEditor';
import _ from 'lodash';

import { OPERATORS_PRIORITY_AST } from 'constants/parser';
import { jsonStringify } from 'utils/json';

/**
 * Recursively lowers the leading character of every expression-tree key (`Type` -> `type`,
 * `TypeOfValue` -> `typeOfValue`), which is what default Newtonsoft PascalCase requires — not a general
 * case-folded lookup, so `TYPEOFVALUE` would still miss. Anything serializing a `CriteriaExpressionItem`
 * with default settings emits that PascalCase: the Lender.Optimus.Ai `propose_final` tool does, and so do
 * the stored expressions of some seeded checklists. Every reader here accesses `type` / `value` /
 * `operands` / `typeOfValue` in camelCase, so without this they read undefined from every node.
 */
export const normalizeAstKeys = value => {
  if (_.isArray(value)) {
    return value.map(normalizeAstKeys);
  }

  if (_.isPlainObject(value)) {
    return _.mapValues(
      _.mapKeys(value, (_v, key) => _.lowerFirst(key)),
      normalizeAstKeys,
    );
  }

  return value;
};

export const cleanFormattedExpression = expression => expression?.replace(/[\t\n\r]/gm, '');

export const buildExpressionTree = (
  userFriendlyExpression,
  { schemaParams, isOnlyBooleanAllowed, isNewExpressionsParsingLogicEnabled = false },
) => {
  const lexicalParser = new LexicalParser(schemaParams);
  const tokens = lexicalParser.parse(cleanFormattedExpression(userFriendlyExpression));

  if (isNewExpressionsParsingLogicEnabled) {
    const newSyntaxParser = new SyntaxParser(schemaParams);

    const { data = {} } = newSyntaxParser.parse({
      tokens,
      isMinimized: true,
      isOnlyBooleanAllowed,
    });

    return jsonStringify(data);
  }

  const { data = {} } = syntaxParser({
    tokens,
    schemaParams,
    isMinimized: true,
    isOnlyBooleanAllowed,
  });

  return jsonStringify(data);
};

export const formatExpression = expression => {
  if (!expression) {
    return expression;
  }

  const modelArg = { getValue: () => expression, getFullModelRange: _.noop };
  const formattedDocument = formatExpressionDocument(modelArg);

  return formattedDocument[0].text;
};

export const getTokenPriority = tokenValue =>
  OPERATORS_PRIORITY_AST.find(({ regex }) => new RegExp(regex).test(tokenValue))?.priority;
